import { Editor } from '@tiptap/react'
import { CSSProperties, FC, useCallback, useMemo, useState } from 'react'
import { Instance } from 'tippy.js'

import styles from './ColorPicker.module.scss'
import Pallete from './Pallete'
import { useColorPicker } from './utils'

import Popup from '~common/Popup'
import ColorIcon from '~icons/editor/font_a.svg'
import { popupSettings } from '~lib/constants'
import Arrow from '~lib/ui-kit/Arrow'

interface ColorPickerProps {
  editor: Editor
}

const ColorPicker: FC<React.PropsWithChildren<ColorPickerProps>> = ({ editor }) => {
  const [instace, setInstance] = useState<Instance>(null)
  const [expanded, setExpanded] = useState(false)

  const {
    fontOptions,
    setFontColor,
    unsetFontColor,
    getCurrentFontColor,
    backgroundOptions,
    setBackgroundColor,
    unsetBackgroundColor,
    getCurrentBackgroundColor,
  } = useColorPicker(editor)

  const style = { background: getCurrentBackgroundColor() || 'unset' } as CSSProperties
  const controls = `tippy-${instace?.id}`

  const handleShow = useCallback(() => {
    setExpanded(true)
  }, [])

  const handleHide = useCallback(() => {
    setExpanded(false)
  }, [])

  const Content = useMemo(
    () => (
      <div className={styles.options}>
        <Pallete
          options={fontOptions}
          label="Font Color"
          onResetColor={unsetFontColor}
          onSetColor={setFontColor}
          className={styles.palette}
        />
        <Pallete
          options={backgroundOptions}
          label="Background Color"
          onResetColor={unsetBackgroundColor}
          onSetColor={setBackgroundColor}
          className={styles.palette}
        />
      </div>
    ),
    [backgroundOptions, fontOptions, setBackgroundColor, setFontColor, unsetBackgroundColor, unsetFontColor]
  )

  return (
    <Popup onShow={handleShow} onHide={handleHide} onCreate={setInstance} content={Content} {...popupSettings}>
      <div className={styles.root} role="combobox" aria-expanded={expanded} aria-controls={controls}>
        <ColorIcon fill={getCurrentFontColor() || 'currentColor'} />
        <span className={styles.backgroundIndicator} style={style} />
        <Arrow as="span" direction={expanded ? 'top' : 'bottom'} />
      </div>
    </Popup>
  )
}

export default ColorPicker
